import React from "react";

import { useMutation, useQuery } from "@apollo/client";
import { Grid, Box, Divider, useTheme, Theme, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { makeStyles, withStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import useDialogState from "@/hooks/useDialogState";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import AddPastLogsButton from "@/pages/Clients/Buttons/AddPastLogsButton";
import { colors } from "@/theme/overrides/colors";
import { displayError } from "@/utils/errors";

import AddWeightGoalModal from "./components/AddCurrentWeightModal";
import CurrentWeight from "./components/CurrentWeight";
import GoalProgress from "./components/GoalProgress";
import PastLogs from "./components/PastLogs";
import WeightChart from "./components/WeightChart";
import WeightGoal from "./components/WeightGoal";

const useStyles = makeStyles()((theme: Theme) => ({
  container: {
    flexGrow: 1,
    height: "100%",
    background: theme.palette.common.white,
    padding: theme.spacing(2),
  },
  flexRow: {
    display: "flex",
    alignItems: "flex-start",
  },
  pastLogsHeadContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
}));

const StyledGridContainer = withStyles(Grid, (theme: Theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(2, 4),
  },
}));

const StyledDivider = withStyles(Divider, (theme: Theme) => ({
  root: {
    background: colors.light,
    width: "2px",
    margin: theme.spacing(0, 2),
  },
}));

export const clientWeightLogsPageQuery = graphql(`
  query ClientWeightLogsPage($date: ISO8601Date, $clientId: ID!) {
    dailyWeights(date: $date, clientId: $clientId) {
      id
      date
      lastWeightKg
      logs {
        ...PastLog
      }
    }
    client(id: $clientId) {
      id
      journalGoal {
        id
        targetWeightKg
      }
      goals {
        nodes {
          id
          title
        }
      }
    }
  }
`);

const updateWeightGoalMutation = graphql(`
  mutation UpdateWeightGoal(
    $updateGoalInput: UpdateGoalInput!
    $updateJournalGoalInput: UpdateJournalGoalInput!
  ) {
    updateGoal(input: $updateGoalInput) {
      goal {
        id
        title
      }
      errors {
        fullMessage
      }
    }
    updateJournalGoal(input: $updateJournalGoalInput) {
      journalGoal {
        id
        targetWeightKg
      }
      errors {
        fullMessage
      }
    }
  }
`);

const deleteCurrentWeightMutation = graphql(`
  mutation DeleteCurrentWeight($input: CreateBodyMeasurementsInput!) {
    createBodyMeasurements(input: $input) {
      bodyMeasurement {
        date
        weightKg
      }
    }
  }
`);

const ClientWeightLogsPage: React.FC = () => {
  const { classes } = useStyles();
  const theme = useTheme();
  const [deleteWeightGoal] = useMutation(updateWeightGoalMutation);
  const [deleteCurrentWeight] = useMutation(deleteCurrentWeightMutation);
  const snackbar = useGlobalSnackbar();
  const addPastLog = useDialogState();
  const { clientId, date } = useParams<{ clientId: string; date: string }>();
  const { data, loading, refetch } = useQuery(clientWeightLogsPageQuery, {
    variables: { clientId: clientId ?? "", date },
  });

  const targetWeight = data?.client?.journalGoal?.targetWeightKg;
  const title = data?.client?.goals.nodes.length
    ? data?.client?.goals.nodes?.[0]?.title
    : "";
  const goalId = data?.client?.goals.nodes.length
    ? data.client.goals.nodes?.[0]?.id
    : "";
  const currentWeight = data?.dailyWeights?.lastWeightKg ?? 0;
  const currentWeightId = data?.dailyWeights?.logs?.length
    ? data?.dailyWeights?.logs?.[0].id
    : "";
  const weightDate = new Date();
  const weightGoal = data?.client?.journalGoal?.targetWeightKg;
  const pastLogs = data?.dailyWeights?.logs ?? [];
  const weightLogGoalPresent = goalId !== "" && weightGoal !== null;
  if (loading) return null;

  function handleDeleteWeightGoal() {
    deleteWeightGoal({
      variables: {
        updateGoalInput: {
          id: goalId,
          clientId: clientId,
          patch: { title: "" },
        },
        updateJournalGoalInput: {
          clientId: clientId,
          attributes: { targetWeightKg: null },
        },
      },
    })
      .then(() => {
        snackbar.open("Goal weight deleted successfully");
        refetch();
      })
      .catch((e) => {
        snackbar.open(displayError(e, "Unable to delete goal weight"));
      });
  }

  function handleDeleteCurrentWeight() {
    deleteCurrentWeight({
      variables: {
        input: {
          clientId: clientId,
          attributes: [{ id: currentWeightId, _destroy: true }],
        },
      },
    })
      .then(() => {
        snackbar.open("Current weight deleted successfully");
        refetch();
      })
      .catch((e) => {
        snackbar.open(displayError(e, "Unable to delete current weight"));
      });
  }

  return (
    <>
      <StyledGridContainer
        container
        spacing={1}
        direction="row"
        alignItems="stretch"
      >
        <Grid item xs={7}>
          <div className={`${classes.container} ${classes.flexRow}`}>
            {/* Weight */}
            <Box width="50%" display="flex" flexDirection="column">
              <Typography variant="body1" fontWeight="bold">
                Weight
              </Typography>
              <Box marginBottom={theme.spacing(1)} />
              <WeightGoal
                weightGoalPresent={weightLogGoalPresent}
                weightGoal={weightGoal}
                title={title}
                onDelete={handleDeleteWeightGoal}
              />
              <Box marginBottom={1} />
              <CurrentWeight
                onDelete={handleDeleteCurrentWeight}
                currentWeight={currentWeight}
                weightDate={weightDate}
                currentWeightId={currentWeightId}
              />
              <GoalProgress
                currentWeight={currentWeight}
                targetWeight={targetWeight}
                logs={pastLogs}
              />
            </Box>

            <StyledDivider orientation="vertical" />

            {/* Past logs */}
            <Box width="50%">
              <div className={classes.pastLogsHeadContainer}>
                <Typography variant="body1" fontWeight="bold">
                  Past Logs
                </Typography>
                <Box display="flex">
                  <AddPastLogsButton onClick={addPastLog.show} />
                </Box>
              </div>
              <PastLogs pastLogs={pastLogs} />
            </Box>
          </div>
        </Grid>

        <Grid item xs={5}>
          <div className={classes.container}>
            <WeightChart
              targetWeight={targetWeight}
              currentWeight={currentWeight}
              loading={loading}
            />
          </div>
        </Grid>
      </StyledGridContainer>

      <AddWeightGoalModal
        onClose={addPastLog.hide}
        visibility={addPastLog.shown}
      />
    </>
  );
};

export default ClientWeightLogsPage;
